import Router from 'next/router';
import { UserProfile } from '../types/AuthTypes';
import { CurrentUserResponse } from '../types/UserTypes';
import { EVENTS, sendEvent, setUserProperties } from './analytics';
import { getCurrentUser, getJWTToken, setReferredBy, sendEmailVerification } from './api';
import isBrowser from './helpers/isBrowser';
import { getToken, removeToken, setToken } from './token';

export const getFirebase = async () => {
  const firebase = await import('firebase/app');
  return firebase;
};

export const loadFirebaseAuth = async () => {
  await import('firebase/auth');
};

function signInProviderToMethod(signInProvider: string | null): string {
  switch (signInProvider) {
    case 'facebook.com':
      return 'facebook';
    case 'google.com':
      return 'google';
    case 'password':
      return 'password';
    default:
      return signInProvider ?? 'unknown';
  }
}

export async function setUserData(): Promise<CurrentUserResponse>;
export async function setUserData(user: UserProfile): Promise<UserProfile>;
export async function setUserData(user?: UserProfile): Promise<UserProfile | CurrentUserResponse> {
  if (!user) {
    user = await getCurrentUser();
  }

  if (process.env.NODE_ENV !== 'test') {
    (await getFirebase()).analytics().setUserProperties({
      impacter: user.roles.IMPACTER ? 'true' : 'false',
      supporter: user.categories.SUPPORTER ? 'true' : 'false',
    });
    setUserProperties({
      id: user.id,
      properties: {
        roles: Object.keys(user.roles),
        ...(user.roles.IMPACTER && { firstName: user.firstName }), // share first name for impacters to speed up analysis
      },
    });
  }

  return user;
}

export async function refreshToken() {
  const currentToken = getToken();
  if (!currentToken) return;

  const idTokenResponse = await getFirebaseToken(true);
  if (!idTokenResponse) return;

  const { token } = await getJWTToken(idTokenResponse.token);
  if (!token) return;

  setToken(token);
}

export async function handleAuthentication(shouldRedirect = true) {
  await loadFirebaseAuth();
  const firebase = await getFirebase();
  if (!firebase.auth().currentUser) {
    return;
  }

  const idTokenResponse = await firebase.auth().currentUser?.getIdTokenResult(true);
  if (!idTokenResponse) {
    return;
  }

  const { token, userCreated } = await getJWTToken(idTokenResponse.token);
  if (!token) {
    return;
  }

  setToken(token);
  const method = signInProviderToMethod(idTokenResponse.signInProvider);
  if (userCreated) {
    firebase.analytics().logEvent('sign_up', { method });
    await sendEvent(EVENTS.signUpAccountCreated.name, { method });
    const invitedBy = localStorage.getItem('@Invite:ReferredBy');
    if (invitedBy) {
      await setReferredBy(invitedBy);
      localStorage.removeItem('@Invite:ReferredBy');
      firebase.analytics().setUserProperties({ referred: 'true' });
    }
  }
  firebase.analytics().logEvent('login', { method });
  await sendEvent(EVENTS.loginLogin.name, { method });
  setUserData();
  linkAccounts();

  const returnUrl = Router.query.returnUrl as string;
  if (returnUrl && shouldRedirect) {
    Router.push(returnUrl);
  }
}

export async function getFirebaseToken(forceRefresh = false) {
  await loadFirebaseAuth();
  const firebase = await getFirebase();
  if (!firebase.auth().currentUser) {
    return;
  }

  const idTokenResponse = await firebase.auth().currentUser?.getIdTokenResult(forceRefresh);
  return idTokenResponse;
}

export async function createEmailAccount(
  email: string,
  password: string,
  firstName: string,
  lastName: string,
) {
  await loadFirebaseAuth();
  const firebase = await getFirebase();
  const credentials = await firebase.auth().createUserWithEmailAndPassword(email, password);
  await credentials.user?.updateProfile({
    displayName: `${firstName} ${lastName}`,
  });
  const firebaseToken = await credentials.user?.getIdToken(true);
  await sendEmailVerification(firebaseToken);
  await sendEvent(EVENTS.firebaseAccountCreatedSuccessful.name, { method: 'password' });
}

export async function logout() {
  await loadFirebaseAuth();
  const firebase = await getFirebase();
  await firebase.auth().signOut();
  removeToken();
  await sendEvent(EVENTS.logout.name);
}

export async function linkAccounts() {
  if (!isBrowser()) {
    return;
  }
  const pendingCredentialString = sessionStorage.getItem('@Auth:pendingCredential');
  if (!pendingCredentialString) {
    return;
  }

  await loadFirebaseAuth();
  const firebase = await getFirebase();
  const pendingCredential = firebase.auth.AuthCredential.fromJSON(pendingCredentialString);
  if (!pendingCredential) {
    return;
  }

  const firebaseUser = firebase.auth().currentUser;
  if (!firebaseUser) {
    return;
  }

  await firebaseUser.linkWithCredential(pendingCredential);
  sessionStorage.removeItem('@Auth:pendingCredential');
}
